import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CustomFieldTypeEnum } from 'src/app/enums/customFieldType.enum';
import { CustomEntityModel } from 'src/app/models/customEntity.model';
import { CustomField } from 'src/app/models/customField.model';
import { ColumnDetails } from 'src/app/models/columnDetails.model';
import { CustomObjectService } from 'src/app/services/customObject.service';
import { CustomEntityService } from 'src/app/services/customEntity.service';
import { ToastService } from 'src/app/services/toast.service';
import { forkJoin, of } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { CustomFieldDialogModalComponent } from 'src/app/modals/custom-field-dialog-modal/custom-field-dialog.component';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import { LocationPickerComponent } from 'src/app/common-components/location-picker/location-picker.component';
import { LocationPickerDialogData } from 'src/app/models/location-picker.model';
import { toOrganizationTree, filterOrganizationTree } from 'src/app/_helpers/org-tree-helper';
import { CustomObjectTypeEnum } from 'src/app/enums/customObjectType.enum';
import { AuthService } from 'src/app/services';
import { PermissionService } from 'src/app/services/permission.service';
import { PermissionFlags } from 'src/app/enums/permissionFlags.enum';

export interface CustomEntityDialogData extends IFormDialogData {
    id: string;
    label: string;
    customObjectId: string;
    type: CustomObjectTypeEnum;
}

@Component({
  standalone: false,
    selector: 'app-node-operations',
    templateUrl: './node-operations.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./node-operations.component.scss'],
})
export class NodeOperationsComponent implements OnInit {

    isFormLoading = false;
    isLoading = false;
    customEntity: CustomEntityModel;
    customFields: (CustomField & { values?: string[]; isDropdown?: boolean })[];
    columnConfig: { [property: string]: ColumnDetails } = {};
    isAdd: boolean;
    orgs: OrganizationLevel[] = [];
    filteredOrgs: OrganizationLevel[] = [];
    orgLevelSearch = '';
    propertyId = 0;
    isGeoDialogOpen: boolean =  false;
    canEditFields: boolean = false;

    constructor(public dialogRef: MatDialogRef<NodeOperationsComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CustomEntityDialogData,
        private customObjectService: CustomObjectService,
        private customEntityService: CustomEntityService,
        private toastService: ToastService,
        private orgService: OrganizationLevelService,
        private dialog: MatDialog,
        private authService: AuthService,
        private permissionService: PermissionService,
    ) {
        this.customEntity = { id: data.id, customObjectId: data.customObjectId } as CustomEntityModel;
        this.isAdd = !this.customEntity.id;
        this.dialogRef.disableClose = false;           
    }

    ngOnInit() {
        this.isFormLoading = true;
        const customObjectId = `COID_${this.data.customObjectId}`;
        this.canEditFields = this.permissionService.hasPermissionFlag(customObjectId, PermissionFlags.UpdateHierarchy);
        forkJoin({
            customFields: this.customObjectService.getCustomObjectFields(this.customEntity.customObjectId),
            customEntity: !this.isAdd ? this.customEntityService.getCustomEntity(this.customEntity.id, this.customEntity.customObjectId) : of(null),
            orgs: this.orgService.getAll(),
            columnConfig: this.customEntityService.getFieldConfiguration(this.customEntity.customObjectId, false)
        }).subscribe({
            next: (result) => {
                this.orgs =  toOrganizationTree(result.orgs);
                this.filteredOrgs = this.orgs;
                this.columnConfig = result.columnConfig || {};
                this.customFields = this.mapCustomFields(result.customFields);
                if (result.customEntity) {
                    this.customEntity = result.customEntity as any;
                }
                this.isFormLoading = false;
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            }
        });
    }

    addEditField(field: CustomField = null, event: MouseEvent) {
        const isAdd = field === null;

        const position = {
            top: `${event.y + 400 > window.innerHeight ? event.y - 370 : event.y}px`,
            left: `${event.x}px`
        };

        const names: string[] = [];
        names.push(...this.customFields.filter(x => x.name !== field?.name).map(x => x.name));
        names.push('id', 'name', 'customObjectId', 'customFields');

        const customFieldDialog = this.dialog.open(CustomFieldDialogModalComponent, {
            data: {
                names: names,
                customField: field ? JSON.parse(JSON.stringify(field)) : new CustomField(),
            },
            width: '450px',
            disableClose: true,
            position: position
        });

        customFieldDialog.afterClosed()
            .subscribe((customField: CustomField) => {
                if (customField && customField.name && customField.type !== undefined) {
                    if (isAdd) {
                        if (!this.customFields) {
                            this.customFields = [];
                        }
                        customField.property = `new_field_${this.propertyId++}`;
                        this.customFields.push(...this.mapCustomFields([customField]));
                    } else {
                        field.name = customField.name;
                        field.type = customField.type;
                        field.data = customField.data;
                        field.isPii = customField.isPii;
                        const [updated] = this.mapCustomFields([field]);
                        const idx = this.customFields.indexOf(field);
                        if (idx !== -1) {
                            this.customFields[idx] = updated;
                        }
                    }
                }
            });

        if (!isAdd) {
            event.stopPropagation();
        }
    }

    mapCustomFields(customFields: CustomField[]): (CustomField & { values?: string[]; isDropdown?: boolean })[] {
        return customFields.map(field => {
            const isDropdown = field.type === CustomFieldTypeEnum.SingleSelect ||
                (field.type === CustomFieldTypeEnum.HrisEmployeeType && !!field.data);
            if (isDropdown) {
                return { ...field, values: field.data.split(',').map(x => x.trim()), isDropdown: true };
            }
            return { ...field, isDropdown: false, values: undefined };
        });
    }

    deleteField(field: CustomField, event: MouseEvent = null) {
        this.customFields.splice(this.customFields.indexOf(field), 1);

        if (event) {
            event.stopPropagation();
        }
    }

    isDisabled(): boolean {
        return !this.customEntity.name?.trim()
            || this.customEntity.organizationLevelId == null
            || this.hasMissingRequiredCustomField();
    }

    private hasMissingRequiredCustomField(): boolean {
        return (this.customFields ?? []).some(field => {
            if (!this.columnConfig[field.property]?.isRequired) {
                return false;
            }
            const value = (this.customEntity as unknown as Record<string, unknown>)[field.property];
            return value == null || (typeof value === 'string' && value.trim() === '');
        });
    }

    save() {
        this.isLoading = true;
        this.customEntity.customFields = this.customFields;
        const saveCustomEntity = this.isAdd
            ? this.customEntityService.create(this.customEntity)
            : this.customEntityService.update(this.customEntity);

        saveCustomEntity
            .subscribe(
                _ => {
                    this.isLoading = false;
                    this.toastService.showSuccess(
                        this.isAdd ? '{{label}} created successfully' : '{{label}} saved successfully',
                        true, { label: this.data.label }
                    );
                    this.customEntity.customFields = undefined;
                    this.dialogRef.close(true);
                },
                error => {
                    this.isLoading = false;
                    this.toastService.showError(error);
                    this.customEntity.customFields = undefined;
                }
            );
    }

    public trackById = trackById;
    public trackByIndex = trackByIndex;

    get submitButtonLabel(): string {
        return this.isAdd ? 'Create' : 'Save';
    }

    get isLocationField(){
        return this.data.type === CustomObjectTypeEnum.LocationEndPoint ? true : false;
    }

    rebuildFilteredOrgs(): void {
        const source = this.orgs ?? [];
        const search = this.orgLevelSearch?.trim().toLowerCase() || '';
        this.filteredOrgs = search
            ? filterOrganizationTree(source, search)
            : source;

    }

    selectGeoLocation() {
        if (this.isGeoDialogOpen) {
            return;
        }
        this.isGeoDialogOpen = true;

        const { latitude, longitude } = this.customEntity;
        const hasCoordinates = typeof latitude === 'number' && typeof longitude === 'number'
            && !(latitude === 0 && longitude === 0);
        const data: LocationPickerDialogData = hasCoordinates
            ? { initialPosition: { lat: latitude, lng: longitude } }
            : { initialCenter: this.getDefaultMapLocation() };
        const dialogRef = this.dialog.open(LocationPickerComponent, {
            width: '600px',
            disableClose: false,
            data,
            hasBackdrop: false,
            panelClass: 'location-picker-panel',
        });

        dialogRef.componentInstance.coordinatesSelected
            .pipe(take(1), takeUntil(dialogRef.afterClosed()))
            .subscribe(coords => {
                this.customEntity.latitude = coords.lat;
                this.customEntity.longitude = coords.lng;
                dialogRef.close();
            });

        dialogRef.afterClosed().subscribe(() => this.isGeoDialogOpen = false);
    }

    private getDefaultMapLocation(): google.maps.LatLngLiteral | undefined {
        const defaultMapLocation = this.authService.getCurrentUser()?.preferences?.defaultMapLocation;
        if (typeof defaultMapLocation !== 'string') {
            return undefined;
        }
        const [lat, lng] = defaultMapLocation.split(',').map(Number);
        return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : undefined;
    }
}
